import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormControl,
  ValidationErrors,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { InventoryItemUOMEvent } from '@inventory/models/edit-inventory.model';
import { ToggleCustomEvent } from '@ionic/angular';
import { PlatformEnum } from '@panamax/app-state';
import {
  GroupPosition,
  InventoryGroupHeader,
  InventoryItemEditOptions,
  InventoryItemModalOptions,
} from '@usf/ngrx-inventory';

@Component({
  selector: 'app-inventory-item-modal',
  templateUrl: './inventory-item-modal.component.html',
  styleUrls: ['./inventory-item-modal.component.scss'],
})
export class InventoryItemModalComponent implements OnInit {
  readonly groupPosition = GroupPosition;
  readonly inventoryItemEditOptions = InventoryItemEditOptions;
  readonly platformEnum = PlatformEnum;

  @Input() inventoryItemModalOptions: InventoryItemModalOptions;
  @Input() platformType: PlatformEnum;
  @Input() isAndroidTouch: boolean;
  @Output()
  changeInventoryItemModalOptionsEmitter: EventEmitter<InventoryItemModalOptions> =
    new EventEmitter();
  @Output()
  openUnitPopoverEmitter: EventEmitter<InventoryItemUOMEvent> =
    new EventEmitter();
  @Output()
  dismissModalEmitter: EventEmitter<boolean> = new EventEmitter();
  @Output()
  nonUSFNavigateEmitter: EventEmitter<void> = new EventEmitter();

  onFocus = false;
  customUnitInput = new FormControl('', [
    Validators.maxLength(8),
    Validators.pattern(new RegExp(/^([a-zA-Z0-9]+ ?)*$/)),
  ]);
  priceOverrideInput = new FormControl('', [
    Validators.pattern(new RegExp(/^\-?.{0,6}(?:\..{0,2})?$/)),
    this.numbersOnly(),
  ]);

  constructor() {}

  ngOnInit(): void {
    this.customUnitInput.patchValue(this.inventoryItemModalOptions.customUnit);
    if (this.inventoryItemModalOptions.overrideCasePrice) {
      this.priceOverrideInput.patchValue(
        this.inventoryItemModalOptions.overrideCasePrice.toString(),
      );
    }
  }

  private numbersOnly(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const numbersRegEx = new RegExp(
        /(^$)|(^[0-9]+)\.?([0-9]+)?$|^\.([0-9]+)?$/,
      );
      return !numbersRegEx.test(control.value)
        ? { numbersOnly: { value: control.value } }
        : null;
    };
  }

  selectOption(selectedOption: InventoryItemEditOptions | string) {
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      selectedOption,
    } as InventoryItemModalOptions);
  }

  selectGroup(selectedGroup: InventoryGroupHeader) {
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      selectedGroup,
      selectedPosition: '',
    } as InventoryItemModalOptions);
  }

  selectPosition(selectedPosition: GroupPosition | string) {
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      selectedPosition,
    } as InventoryItemModalOptions);
  }

  openUnitPopover(event: Event) {
    this.openUnitPopoverEmitter.emit({
      event: event,
      catchWeightFlag: this.inventoryItemModalOptions.catchWeightFlag,
      customUOM: this.inventoryItemModalOptions.customUnit,
    });
  }

  changeCustomUnit(event: any) {
    const customUnit = String(event?.currentTarget?.value);
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      customUnit,
    } as InventoryItemModalOptions);
  }

  changeInvConvFactor(event: any) {
    const invConvFactor = Number(event?.currentTarget?.value);
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      invConvFactor,
    } as InventoryItemModalOptions);
  }

  changePriceOverrideInd(event: Event) {
    const toggleCustomEvent = event as ToggleCustomEvent;
    let updatedInventoryItemModalOptions = {
      ...this.inventoryItemModalOptions,
    };
    if (toggleCustomEvent.detail.checked) {
      const value = this.priceOverrideInput.getRawValue();
      updatedInventoryItemModalOptions = {
        ...updatedInventoryItemModalOptions,
        priceOverrideInd: 'Y',
        overrideCasePrice: !!value ? Number(value) : undefined,
      };
    } else {
      updatedInventoryItemModalOptions = {
        ...updatedInventoryItemModalOptions,
        priceOverrideInd: 'N',
        overrideCasePrice: undefined,
      };
    }
    this.changeInventoryItemModalOptionsEmitter.emit(
      updatedInventoryItemModalOptions,
    );
  }

  changeOverrideCasePrice(event: any) {
    const overrideCasePrice = Number(event?.currentTarget?.value);
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      overrideCasePrice,
    } as InventoryItemModalOptions);
  }

  dismissModal() {
    this.dismissModalEmitter.emit(false);
  }

  saveItemChanges() {
    this.dismissModalEmitter.emit(true);
  }

  inputFocus() {
    this.onFocus = true;
  }

  inputBlur() {
    this.onFocus = false;
  }

  openPDPOverlay() {
    if (this.inventoryItemModalOptions.nonUSFFlag) {
      this.nonUSFNavigateEmitter.emit();
    } else {
      this.changeInventoryItemModalOptionsEmitter.emit({
        ...this.inventoryItemModalOptions,
        isPDPOverlayOpen: true,
      } as InventoryItemModalOptions);
    }
  }

  closePDPOverlay() {
    this.dismissModalEmitter.emit(false);
    this.changeInventoryItemModalOptionsEmitter.emit({
      ...this.inventoryItemModalOptions,
      isPDPOverlayOpen: false,
    } as InventoryItemModalOptions);
  }
}
